import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";

export const ProductCard = ({ product, index = 0 }) => {
  const navigate = useNavigate();
  const { lang } = useLang();

  const L = {
    en: { view: "VIEW PRODUCT", from: "FROM", stock: "units left", always: "Always in stock" },
    it: { view: "VEDI PRODOTTO", from: "DA", stock: "pezzi disponibili", always: "Sempre disponibile" },
  }[lang];

  const go = () => {
    navigate(`/product/${product.slug}`);
    window.scrollTo({ top: 0, behavior: "instant" });
  };

  return (
    <motion.button
      onClick={go}
      data-testid={`product-card-${product.slug}`}
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.8, delay: index * 0.12, ease: [0.2, 0.6, 0.1, 1] }}
      whileHover={{ y: -4 }}
      className="group relative text-left bg-[#0a0a0a] border border-[#222] hover:border-wood/60 transition-colors overflow-hidden"
    >
      {/* Image */}
      <div className="relative aspect-[4/3] bg-ink overflow-hidden">
        <div className="absolute inset-0 blueprint-grid opacity-20" />
        <motion.img
          src={product.cover}
          alt={product.name}
          className="absolute inset-0 w-full h-full object-contain p-8 md:p-12"
          initial={{ scale: 1 }}
          whileHover={{ scale: 1.04 }}
          transition={{ duration: 0.6 }}
        />
        {/* Corner ticks */}
        <span className="absolute top-3 left-3 w-3 h-px bg-wood" />
        <span className="absolute top-3 left-3 w-px h-3 bg-wood" />
        <span className="absolute top-3 right-3 w-3 h-px bg-wood" />
        <span className="absolute top-3 right-3 w-px h-3 bg-wood" />
        <span className="absolute bottom-3 left-3 w-3 h-px bg-wood" />
        <span className="absolute bottom-3 left-3 w-px h-3 bg-wood" />
        <span className="absolute bottom-3 right-3 w-3 h-px bg-wood" />
        <span className="absolute bottom-3 right-3 w-px h-3 bg-wood" />

        {/* Fitment badge */}
        <div className="absolute top-4 right-4 font-mono text-[10px] tracking-tech text-wood bg-ink/80 border border-wood/40 px-2.5 py-1">
          {product.subtitle}
        </div>
      </div>

      {/* Info */}
      <div className="p-6 md:p-8 border-t border-[#222]">
        <div className="flex items-baseline justify-between gap-4 mb-2">
          <h3 className="font-display text-2xl md:text-3xl tracking-brutal uppercase leading-tight">
            {product.name}
          </h3>
        </div>
        <p className="font-mono text-[11px] tracking-tech text-bone/50 mb-6">
          SKU / {product.sku}
        </p>

        <div className="flex items-end justify-between gap-4">
          <div>
            <div className="font-mono text-[10px] tracking-tech text-bone/50 mb-1">
              {L.from}
            </div>
            <div className="font-display text-4xl md:text-5xl text-wood tracking-brutal leading-none">
              €{product.price_eur}
            </div>
            <div className="font-mono text-[10px] tracking-tech text-bone/40 mt-2">
              {product.always_in_stock ? L.always : `${product.current_stock} ${L.stock}`}
            </div>
          </div>
          <span className="font-mono text-xs tracking-tech text-bone/70 group-hover:text-wood transition-colors whitespace-nowrap">
            {L.view} →
          </span>
        </div>
      </div>
    </motion.button>
  );
};

export default ProductCard;
